import { notifyContent, state, subscribeStructure } from '../state';
import { mountTargetsSection } from './targets-section';

interface Elements {
  container: HTMLElement;
}

// Keys for non-persisted UI state (collapsed sections).
const COLLAPSED_KEY = 'espolon-inputs-collapsed';

interface CollapsedState {
  targets: boolean;
  opening: boolean;
  closing: boolean;
  template: boolean;
}

function loadCollapsed(): CollapsedState {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    if (!raw) return { targets: false, opening: true, closing: true, template: true };
    const parsed = JSON.parse(raw) as Partial<CollapsedState>;
    return {
      targets: !!parsed.targets,
      opening: parsed.opening ?? true,
      closing: parsed.closing ?? true,
      template: parsed.template ?? true,
    };
  } catch {
    return { targets: false, opening: true, closing: true, template: true };
  }
}

function saveCollapsed(state: CollapsedState): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
}

export function mountInputsPanel({ container }: Elements): void {
  const collapsed = loadCollapsed();

  container.innerHTML = `
    <section class="inputs-section" id="targets-section" data-section="targets"></section>
    <section class="inputs-section prompt-section prompt-section-collapsible" id="opening-prompt-section" data-section="opening">
      <header class="prompt-section-header" data-action="toggle">
        <span class="prompt-section-label">Opening Prompt</span>
        <span class="prompt-section-toggle"></span>
      </header>
      <textarea class="template-textarea inputs-textarea" id="opening-prompt" placeholder="Shared opening context prepended to every area's prompt..."></textarea>
    </section>
    <section class="inputs-section prompt-section prompt-section-collapsible" id="closing-prompt-section" data-section="closing">
      <header class="prompt-section-header" data-action="toggle">
        <span class="prompt-section-label">Closing Prompt</span>
        <span class="prompt-section-toggle"></span>
      </header>
      <textarea class="template-textarea inputs-textarea" id="closing-prompt" placeholder="Shared closing context appended to every area's prompt..."></textarea>
    </section>
    <section class="inputs-section prompt-section prompt-section-collapsible" id="target-template-section" data-section="template">
      <header class="prompt-section-header" data-action="toggle">
        <span class="prompt-section-label">Target Template</span>
        <span class="inputs-hint" id="target-template-hint"></span>
        <span class="prompt-section-toggle"></span>
      </header>
      <textarea class="template-textarea inputs-textarea" id="target-template" placeholder="replace [DESCRIPTION] with [UPDATE]"></textarea>
    </section>
  `;

  const targetsSection = container.querySelector<HTMLElement>('#targets-section')!;
  const openingSection = container.querySelector<HTMLElement>('#opening-prompt-section')!;
  const closingSection = container.querySelector<HTMLElement>('#closing-prompt-section')!;
  const templateSection = container.querySelector<HTMLElement>('#target-template-section')!;
  const openingTa = container.querySelector<HTMLTextAreaElement>('#opening-prompt')!;
  const closingTa = container.querySelector<HTMLTextAreaElement>('#closing-prompt')!;
  const templateTa = container.querySelector<HTMLTextAreaElement>('#target-template')!;
  const templateHint = container.querySelector<HTMLElement>('#target-template-hint')!;

  mountTargetsSection({
    container: targetsSection,
    collapsed: collapsed.targets,
    onCollapsedChange: (val) => {
      collapsed.targets = val;
      saveCollapsed(collapsed);
    },
  });

  openingSection.classList.toggle('collapsed', collapsed.opening);
  closingSection.classList.toggle('collapsed', collapsed.closing);
  templateSection.classList.toggle('collapsed', collapsed.template);

  const syncFromState = (): void => {
    if (openingTa.value !== state.openingPrompt) openingTa.value = state.openingPrompt;
    if (closingTa.value !== state.closingPrompt) closingTa.value = state.closingPrompt;
    if (templateTa.value !== state.targetTemplate) templateTa.value = state.targetTemplate;
    updateTemplateHint(templateHint);
    applyEditVisibility(container);
  };

  openingTa.value = state.openingPrompt;
  closingTa.value = state.closingPrompt;
  templateTa.value = state.targetTemplate;
  updateTemplateHint(templateHint);
  applyEditVisibility(container);

  openingTa.addEventListener('input', () => {
    state.openingPrompt = openingTa.value;
    notifyContent();
  });
  closingTa.addEventListener('input', () => {
    state.closingPrompt = closingTa.value;
    notifyContent();
  });
  templateTa.addEventListener('input', () => {
    state.targetTemplate = templateTa.value;
    updateTemplateHint(templateHint);
    notifyContent();
  });

  container.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    const trigger = e.target.closest<HTMLElement>('[data-action="toggle"]');
    if (!trigger) return;
    const section = trigger.closest<HTMLElement>('[data-section]');
    if (!section) return;
    const key = section.dataset.section as keyof CollapsedState | undefined;
    if (!key || key === 'targets') return;
    section.classList.toggle('collapsed');
    collapsed[key] = section.classList.contains('collapsed');
    saveCollapsed(collapsed);
  });

  subscribeStructure(syncFromState);
}

function applyEditVisibility(container: HTMLElement): void {
  container.style.display = state.questionsEditMode ? '' : 'none';
}

function updateTemplateHint(el: HTMLElement): void {
  const t = state.targetTemplate;
  const missing: string[] = [];
  if (!t.includes('[DESCRIPTION]')) missing.push('[DESCRIPTION]');
  if (!t.includes('[UPDATE]')) missing.push('[UPDATE]');
  if (missing.length === 0) {
    el.textContent = '';
    el.classList.remove('is-warning');
  } else {
    el.textContent = `missing ${missing.join(' + ')}`;
    el.classList.add('is-warning');
  }
}
